import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ClockIcon, DollarSignIcon, GraduationCapIcon, LayersIcon, MonitorIcon, ShieldCheckIcon, UserIcon } from 'lucide-react';
import { HeroMatchForm, type MatchFormProgram } from '../components/HeroMatchForm';
import { LegalFooter } from '../components/LegalFooter';
import { usePageMeta } from '../hooks/usePageMeta';
import { getAreaHeroImage, getRelatedLevels, getRelatedPrograms, type DegreeProgramCombo } from '../utils/degreeSlugs';
interface ProgramInformationComboPageProps {
  combo: DegreeProgramCombo;
}
function toOption(combo: DegreeProgramCombo): MatchFormProgram {
  return {
    slug: combo.slug,
    name: combo.programLabel,
    credential: combo.degreeLabel
  };
}

/**
 * Generated program-information page for one degree level × program
 * combination, using the same in-hero match form as the hand-written program
 * pages — TCPA Search after the location step, then TCPA Post plus Leads and
 * Clicks Search on submit.
 *
 * Copy is assembled from the combination itself rather than hand-written, so
 * every combination has a page without claiming specifics: no invented tuition
 * figures, salaries, program lengths, or school names.
 */
export function ProgramInformationComboPage({
  combo
}: ProgramInformationComboPageProps) {
  const heroImage = getAreaHeroImage(combo.areaStudy);
  const relatedLevels = getRelatedLevels(combo);
  const relatedPrograms = getRelatedPrograms(combo);

  // The contact step has the most fields, so the form widens once the visitor
  // clears the ZIP question rather than squeezing them into a narrow column.
  const [formExpanded, setFormExpanded] = useState(false);
  const handleFormStep = useCallback((step: number) => {
    setFormExpanded(step >= 3);
  }, []);
  usePageMeta(`Online ${combo.title} Programs | Online College Guide`, `Compare accredited online ${combo.programLabel} programs. Get matched by career goals, schedule, and budget.`, {
    ogType: 'article'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [combo.slug]);
  const scrollToForm = () => {
    document.getElementById('hero-match-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };

  // Sibling combinations fill the picker so a visitor who picked the wrong
  // program can correct it without leaving the page.
  const pickerOptions: MatchFormProgram[] = [toOption(combo), ...relatedPrograms.map(toOption)];
  const matchBySlug = new Map<string, DegreeProgramCombo>([[combo.slug, combo], ...relatedPrograms.map(c => [c.slug, c] as [string, DegreeProgramCombo])]);
  const facts = [{
    icon: GraduationCapIcon,
    label: 'Credential',
    value: combo.degreeLabel
  }, {
    icon: LayersIcon,
    label: 'Field',
    value: combo.areaLabel
  }, {
    icon: MonitorIcon,
    label: 'Format',
    value: 'Online'
  }, {
    icon: ClockIcon,
    label: 'Start Dates',
    value: 'Year-round'
  }];
  const reasons = [{
    icon: ShieldCheckIcon,
    title: 'Accredited schools only',
    body: `Every ${combo.programLabel.toLowerCase()} program we match you with comes from an accredited institution, so your credits and credential hold their value.`
  }, {
    icon: ClockIcon,
    title: 'Built around your schedule',
    body: 'Online coursework lets you keep working while you study, with start dates throughout the year rather than one intake each fall.'
  }, {
    icon: DollarSignIcon,
    title: 'Aid you may qualify for',
    body: 'Accredited schools eligible for federal aid can accept the FAFSA. Ask each school what applies to your situation once you are matched.'
  }];
  return <main className="min-h-screen bg-white" data-id="element-2400" data-mp="3bsei19zex8hg">
      {/* Hero. The photograph shows the work this field leads to; the navy base
          and scrim keep the headline legible while it loads. */}
      <section className="relative bg-navy overflow-hidden" data-id="element-2401" data-mp="3kgk3spg8jsch">
        {heroImage && <>
            <img src={heroImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-center lg:scale-x-[-1]" data-id="element-2402" data-mp="3pf3pqputx44u" />
            <div className="absolute inset-0 bg-navy/80" aria-hidden="true" data-id="element-2403" data-mp="30283ckryagy" />
          </>}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-7 lg:py-20" data-id="element-2404" data-mp="7zn7fo3uwku7">
          <nav aria-label="Breadcrumb" className="hidden lg:block mb-6" data-id="element-2405" data-mp="bzxopwx3mxod">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-2406" data-mp="c2bjs5rs49vx">
              <li data-id="element-2407" data-mp="gxh10ra8l0do">
                <Link to="/program-information" className="hover:text-white transition-colors duration-200" data-id="element-2408" data-mp="lymuwsfmutos">
                  Program Information
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-2409" data-mp="2nr2n7ffpslw0">/</li>
              <li className="text-white/90" data-id="element-2410" data-mp="2ns43pzfi20l4">{combo.areaLabel}</li>
            </ol>
          </nav>

          {/* Explicit grid placement rather than a duplicated form: on mobile
              the form follows the headline so it lands within reach. */}
          <div className="grid gap-y-5 lg:grid-cols-5 lg:gap-y-7 lg:gap-x-14 items-start" data-id="element-2411" data-mp="26hqopx37jtdw">
            <div className={`${formExpanded ? 'lg:col-span-5 lg:col-start-1' : 'lg:col-span-3 lg:col-start-1'} lg:row-start-1`} data-id="element-2412" data-mp="21idsilqg59yn">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] font-semibold uppercase tracking-widest text-gold mb-3 lg:mb-5" data-id="element-2413" data-mp="2z30d2zy2b1re">
                <GraduationCapIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2414" data-mp="2p8zzsasmmq7l" />
                {combo.degreeLabel}
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-2415" data-mp="1dsjiebxihzi3">
                Online {combo.title} Programs
              </h1>
            </div>

            <div id="hero-match-form" className={formExpanded ? 'lg:col-span-5 lg:col-start-1 lg:row-start-2' : 'lg:col-span-2 lg:col-start-4 lg:row-start-1 lg:row-span-2'} data-id="element-2416" data-mp="trb9i095g7k">
              <HeroMatchForm programs={pickerOptions} initialProgramSlug={combo.slug} matchFor={program => {
              const match = matchBySlug.get(program.slug) || combo;
              return {
                degreeInterest: match.degreeInterest,
                areaStudy: match.areaStudy,
                mProgram: match.mProgram
              };
            }} onStepChange={handleFormStep} wide={formExpanded} data-id="element-2417" data-mp="3n329rd7mfwcj" />
            </div>

            <p className={`${formExpanded ? 'lg:col-span-5 lg:col-start-1 lg:row-start-3' : 'lg:col-span-3 lg:col-start-1 lg:row-start-2'} text-base lg:text-lg text-white/80 leading-relaxed max-w-2xl`} data-id="element-2418" data-mp="2uckbqqlr8l5z">
              Compare accredited online {combo.programLabel.toLowerCase()}{' '}
              programs at the {combo.degreeLabel.toLowerCase()} level. Answer a
              few questions and we'll connect you with schools that fit your
              schedule and budget.
            </p>

            <ul className={`${formExpanded ? 'lg:col-span-5 lg:col-start-1 lg:row-start-4' : 'lg:col-span-3 lg:col-start-1 lg:row-start-3'} flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80`} data-id="element-2419" data-mp="2kqhpdcizjiy9">
              {['Accredited schools', 'Flexible online study', 'No obligation'].map(item => <li key={item} className="flex items-center gap-2" data-id="element-2420" data-mp="33eeasckr36eh">
                    <CheckIcon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" data-id="element-2421" data-mp="33h8v843rkr7w" />
                    {item}
                  </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Fact row. Only what the combination itself establishes — no invented
          length, credit, or tuition figures. */}
      <section className="border-b border-gray-200 bg-light" data-id="element-2422" data-mp="27ye9dswkr1xm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2423" data-mp="27x33nnjkjk3v">
          <dl className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2424" data-mp="2cxifubs46lk9">
            {facts.map(fact => <div key={fact.label} className="flex items-start gap-3" data-id="element-2425" data-mp="1fbth8q89ylt0">
                <fact.icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-crimson" aria-hidden="true" data-id="element-2426" data-mp="1kaehuqrfv7hz" />
                <div data-id="element-2427" data-mp="1p6lbpl6k8sjk">
                  <dt className="text-[11px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-2428" data-mp="1tywgp2g5r4iz">
                    {fact.label}
                  </dt>
                  <dd className="text-sm font-bold text-gray-900 mt-0.5" data-id="element-2429" data-mp="wjik7d0axi2t">
                    {fact.value}
                  </dd>
                </div>
              </div>)}
          </dl>
        </div>
      </section>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-id="element-2430" data-mp="3ar08f4lglzz5">
        <div className="grid lg:grid-cols-3 gap-12" data-id="element-2431" data-mp="3app5f6zu8w93">
          <div className="lg:col-span-2 space-y-12" data-id="element-2432" data-mp="35rdqihuorvk0">
            <section aria-labelledby="overview-heading" data-id="element-2433" data-mp="30tca64onaac8">
              <h2 id="overview-heading" className="text-2xl font-bold text-navy mb-5" data-id="element-2434" data-mp="30su64lvqqrtr">
                About This Program
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2435" data-mp="2r2r4jxak05jt">
                <p data-id="element-2436" data-mp="2n1fnwh9nqunr">
                  An online {combo.degreeLabel.toLowerCase()} in{' '}
                  {combo.programLabel.toLowerCase()} sits within{' '}
                  {combo.areaLabel.toLowerCase()}, and is designed for students
                  who need to study around work and family rather than on a
                  campus schedule.
                </p>
                <p data-id="element-2437" data-mp="28713ccag3d6z">
                  Coursework, assessment, and faculty support are delivered
                  online. Requirements vary by school — some programs in this
                  field include an externship, practicum, or supervised hours
                  that must be completed in person, so confirm that with each
                  school before you enroll.
                </p>
                <p data-id="element-2438" data-mp="12n7lyvarp5py">
                  What matters to employers is the school's accreditation and
                  the skills you gain, not whether you attended in person or
                  online.
                </p>
              </div>
            </section>

            <section aria-labelledby="consider-heading" data-id="element-2439" data-mp="nwqkjby4uft2">
              <h2 id="consider-heading" className="text-2xl font-bold text-navy mb-5" data-id="element-2440" data-mp="ix464z6kitio">
                What to Compare Between Schools
              </h2>
              <ul className="space-y-3" data-id="element-2441" data-mp="1uc9m34vpmok8">
                {['Accreditation — the agency, and whether it is recognized by the U.S. Department of Education or CHEA.', 'Program length and how many credits transfer in from prior study.', 'Whether any in-person hours, labs, or externships are required.', 'Total cost, and which federal aid, scholarships, or employer tuition benefits apply.', 'Start dates, course pacing, and how much of the work is scheduled versus self-paced.', 'Support included — advising, tutoring, career services, and technical help.'].map(item => <li key={item} className="flex items-start" data-id="element-2442" data-mp="2btzv0jh80a6y">
                    <CheckIcon className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5 text-crimson" aria-hidden="true" data-id="element-2443" data-mp="2brlxm3z2ilw8" />
                    <span className="text-gray-700" data-id="element-2444" data-mp="34gswj42fpdi0">{item}</span>
                  </li>)}
              </ul>
            </section>

            <section aria-labelledby="why-heading" data-id="element-2445" data-mp="3j52mokfvqpgz">
              <h2 id="why-heading" className="text-2xl font-bold text-navy mb-5" data-id="element-2446" data-mp="3j4kjt4yppd03">
                Why study {combo.programLabel.toLowerCase()} online?
              </h2>
              <div className="grid sm:grid-cols-3 gap-8" data-id="element-2447" data-mp="39654dz91rs6f">
                {reasons.map(reason => <div key={reason.title} data-id="element-2448" data-mp="3562zlh80mimx">
                    <reason.icon className="w-6 h-6 text-crimson mb-3" aria-hidden="true" data-id="element-2449" data-mp="357e45j9abuf3" />
                    <h3 className="text-base font-bold text-navy mb-2" data-id="element-2450" data-mp="2vf8gglq2fz5p">
                      {reason.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2451" data-mp="2qgor0bc9k58l">
                      {reason.body}
                    </p>
                  </div>)}
              </div>
            </section>
          </div>

          {/* Sidebar. Sticky so it travels with the reader instead of leaving a
              tall empty column beside the content. */}
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start" data-id="element-2452" data-mp="rxjx3tg90252">
            <div className="border border-gray-200 rounded-2xl p-6 bg-light" data-id="element-2453" data-mp="wxg481k8s9ou">
              <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-2454" data-mp="wziy4woimmyu">
                At a Glance
              </h2>
              <dl className="space-y-3 text-sm" data-id="element-2455" data-mp="15ymie3zf58ex">
                <div className="flex justify-between gap-4" data-id="element-2456" data-mp="15w0ecebqlb1g">
                  <dt className="text-gray-500" data-id="element-2457" data-mp="99ewuvysuzb0">Credential</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2458" data-mp="d9fltajiyswb">
                    {combo.degreeLabel}
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2459" data-mp="i7h6u10ih2af">
                  <dt className="text-gray-500" data-id="element-2460" data-mp="mzs93aiq5065">Program</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2461" data-mp="3lpijqoku537g">
                    {combo.programLabel}
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2462" data-mp="3qn0vemq8lwj5">
                  <dt className="text-gray-500" data-id="element-2463" data-mp="1x5d0fut5b5tk">Field</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2464" data-mp="1x3s7g9mhbuxt">
                    {combo.areaLabel}
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2465" data-mp="25ygeo5bm7mp9">
                  <dt className="text-gray-500" data-id="element-2466" data-mp="217hs4o019sdk">Format</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2467" data-mp="215wyr1pggfha">
                    Online
                  </dd>
                </div>
              </dl>
              <button type="button" onClick={scrollToForm} className="mt-6 w-full bg-cta hover:bg-cta-dark text-white font-bold py-3 px-5 rounded-lg text-sm uppercase tracking-wide transition-colors duration-200" data-id="element-2468" data-mp="2axsxmezd89mk">
                Get Matched Now
              </button>
            </div>

            {relatedLevels.length > 0 && <div className="border border-gray-200 rounded-2xl p-6" data-id="element-2469" data-mp="qhxz479z4kp8">
                <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-2470" data-mp="qjsnjvkgeuy2">
                  Other Levels
                </h2>
                <ul className="space-y-3" data-id="element-2471" data-mp="ke1gehh2i77k">
                  {relatedLevels.map(item => <li key={item.slug} data-id="element-2472" data-mp="6ohh5lmp3s30">
                      <Link to={`/program-information/${item.slug}`} className="group flex items-start justify-between gap-3" data-id="element-2473" data-mp="6lln48dyxnju">
                        <span className="text-sm text-gray-700 group-hover:text-navy transition-colors duration-200" data-id="element-2474" data-mp="3sx0nkfzoesmg">
                          {item.programLabel}
                          <span className="block text-xs text-gray-500" data-id="element-2475" data-mp="3jv9gp7tegfr0">
                            {item.degreeLabel}
                          </span>
                        </span>
                        <ArrowRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-200" aria-hidden="true" data-id="element-2476" data-mp="1i2lcav50rm9n" />
                      </Link>
                    </li>)}
                </ul>
              </div>}

            <div className="border border-gray-200 rounded-2xl p-6" data-id="element-2477" data-mp="1bml71t4ywywu">
              <UserIcon className="w-6 h-6 mb-3 text-crimson" aria-hidden="true" data-id="element-2478" data-mp="1bmuwnfm3p420" />
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2479" data-mp="16otcssgup0ph">
                Not sure which program fits? Search every degree level and field
                in one place.
              </p>
              <Link to="/program-information" className="mt-4 inline-flex items-center text-sm font-semibold text-crimson hover:underline" data-id="element-2480" data-mp="1zdi7a8nei7s9">
                Browse all programs
                <ArrowRightIcon className="w-4 h-4 ml-1.5" aria-hidden="true" data-id="element-2481" data-mp="1pk9sb22y682f" />
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {relatedPrograms.length > 0 && <section aria-labelledby="related-heading" className="border-t border-gray-200 bg-light" data-id="element-2482" data-mp="2i8yja8aagx0g">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-id="element-2483" data-mp="2ul0yyttwwc3p">
            <h2 id="related-heading" className="text-xl sm:text-2xl font-bold text-navy mb-1.5" data-id="element-2484" data-mp="2undg54ruiifp">
              Other {combo.areaLabel.toLowerCase()} programs
            </h2>
            <p className="text-sm text-gray-600 mb-6" data-id="element-2485" data-mp="38l208ae39ljp">
              Still deciding? These {combo.degreeLabel.toLowerCase()} programs
              sit in the same field.
            </p>
            <ul className="flex flex-wrap gap-2.5" data-id="element-2486" data-mp="3dhsecs6c272h">
              {relatedPrograms.map(item => <li key={item.slug} data-id="element-2487" data-mp="3id7r1gsltwxb">
                  <Link to={`/program-information/${item.slug}`} className="group inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full pl-4 pr-3 py-2.5 text-sm font-medium text-navy hover:border-navy hover:shadow-sm transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-2488" data-mp="3na7vjn9t2rmb">
                    {item.programLabel}
                    <ArrowRightIcon className="w-3.5 h-3.5 text-gray-400 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-navy" aria-hidden="true" data-id="element-2489" data-mp="hec4cw3pb38t" />
                  </Link>
                </li>)}
            </ul>
          </div>
        </section>}

      {/* Bottom CTA */}
      <section className="bg-navy py-14" data-id="element-2490" data-mp="3txldzxaw7wxt">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2491" data-mp="cfio4vx14y4y">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-2492" data-mp="chloo9vt69nx">
            Ready to Take the Next Step?
          </h2>
          <p className="text-white/80 leading-relaxed mb-8" data-id="element-2493" data-mp="3g4lznymuk8ru">
            Get matched with accredited schools offering online{' '}
            {combo.programLabel.toLowerCase()} programs at the{' '}
            {combo.degreeLabel.toLowerCase()} level. No cost, and no obligation.
          </p>
          <button type="button" onClick={scrollToForm} className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-10 rounded-lg text-sm tracking-wide uppercase transition-colors duration-200" data-id="element-2494" data-mp="1qlne6o7oafgm">
            Get Matched Now
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2495" data-mp="29dybm2jk5l7c" />
          </button>
        </div>
      </section>

      <LegalFooter data-id="element-2496" data-mp="2edkrkjqrtfju" />
    </main>;
}